import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { lighten } from '@mui/material/styles'
import {
  Box,
  Typography,
  List,
  ListItem,
  IconButton,
  ListItemText,
  Button,
  Alert,
  CircularProgress,
  TextField,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import { UUID } from 'crypto'

import { useFundingNeedDocs } from '@/utils/hooks/fundingNeeds'
import { useUpdateOrgWebsiteMutation } from '@/utils/hooks/orgs'
import { isRootUrl } from '@/utils/utils'
import { isDuplicateWebsite } from '@/utils/uploadDedup'
import { useSSEProgress } from '@/pages/onboarding/hooks/useSSEProgress'
import { WebsiteUploadResult } from '@/models/Orgs'
import {
  formatWebsiteUploadError,
  formatWebsiteFailureLine,
} from '@/utils/websiteUploadErrors'
import { isBlockedUrl, getBlockedUrlMessage } from '@/utils/urlValidation'

export default function UploadFundingNeedWebsiteUrls({
  orgUuid,
  token,
  fundingNeedId,
  orgWebsite,
  onPendingChange,
}: {
  orgUuid: UUID
  token: string
  fundingNeedId: number
  orgWebsite?: string | null
  // Lets a parent form (which may have its own, separate "Save" action)
  // know URLs are staged here but not yet uploaded, so it isn't saved
  // over without warning. See #1465.
  onPendingChange?: (hasPending: boolean) => void
}) {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const [newWebsite, setNewWebsite] = useState('')
  const [websites, setWebsites] = useState<string[]>([])
  const [uploaded, setUploaded] = useState<string[] | null>(null)
  const [uploadError, setUploadError] = useState<string | null>(null)
  const [uploadWarnings, setUploadWarnings] = useState<string[]>([])
  const [uploading, setUploading] = useState(false)
  const [pendingWebsite, setPendingWebsite] = useState<string | null>(null)
  const [jobId, setJobId] = useState<string | null>(null)

  const [addWebsiteError, setAddWebsiteError] = useState<string | null>(null)
  const updateOrgWebsite = useUpdateOrgWebsiteMutation()

  const { data: fundingNeedDocs, uploadFundingNeedWebsites, refetch: refetchDocs } =
    useFundingNeedDocs(orgUuid, token as string, fundingNeedId)
  const existingWebsiteUrls =
    fundingNeedDocs
      ?.filter((d) => d.website_url)
      .map((d) => d.website_url as string) ?? []

  const validateWebsite = (website: string) => {
    const protocolRegex = /^https?:\/\//
    if (!protocolRegex.test(website)) {
      // add protocol if not present
      website = `http://${website}`
    }

    try {
      new URL(website) // check if it's a valid URL
      return true
    } catch {
      return false
    }
  }

  const formatWebsiteURL = (website: string) => {
    // Add http:// prefix if not present
    if (!website.startsWith('http://') && !website.startsWith('https://')) {
      return `http://${website}`
    }
    return website
  }

  const handleAddWebsite = () => {
    if (websites.length >= 5) {
      setAddWebsiteError(
        'Maximum websites that can be uploaded at one time is 5',
      )
      return
    }

    if (validateWebsite(newWebsite)) {
      const formattedURL = formatWebsiteURL(newWebsite)
      // Immediate feedback only — the backend is the gate that actually rejects.
      if (isBlockedUrl(formattedURL)) {
        setAddWebsiteError(getBlockedUrlMessage())
        return
      }
      // Block URLs already staged or already uploaded for this funding need (the
      // latter would be rejected by the backend on upload).
      if (isDuplicateWebsite(formattedURL, websites, existingWebsiteUrls)) {
        const alreadyUploaded = isDuplicateWebsite(
          formattedURL,
          [],
          existingWebsiteUrls,
        )
        setAddWebsiteError(
          alreadyUploaded
            ? 'This website has already been uploaded for this funding request.'
            : 'Website URL already exists in the list to upload.',
        )
        return
      }
      if (!orgWebsite && isRootUrl(formattedURL)) {
        setPendingWebsite(formattedURL)
        setNewWebsite('')
        setAddWebsiteError(null)
        return
      }
      setWebsites([...websites, formattedURL])
      setNewWebsite('')
      setAddWebsiteError(null)
    } else {
      setAddWebsiteError(
        'Invalid website URL. Please enter a valid domain (e.g., example.com)',
      )
    }
  }

  const handleConfirmIsOrgWebsite = async () => {
    if (!pendingWebsite) return
    try {
      await updateOrgWebsite.mutateAsync({
        token,
        orgUuid,
        website: pendingWebsite,
      })
    } catch {
      // Non-fatal — proceed with upload even if saving the website field fails
    }
    setWebsites((prev) => [...prev, pendingWebsite])
    setPendingWebsite(null)
  }

  const handleConfirmNotOrgWebsite = () => {
    if (!pendingWebsite) return
    setWebsites((prev) => [...prev, pendingWebsite])
    setPendingWebsite(null)
  }

  const uploadProgress = useSSEProgress({
    type: 'upload',
    jobId,
    token,
    onError: (message) => {
      setUploadError(
        formatWebsiteUploadError(
          message,
          'Error uploading websites - Please try again later.',
        ),
      )
      setUploading(false)
      setJobId(null)
    },
  })

  const applyUploadResults = (results: WebsiteUploadResult[]) => {
    const successful = results.filter((r) => r.success)
    const failed = results.filter((r) => !r.success)

    const allWarnings = successful.flatMap((r) => r.warnings || [])
    if (allWarnings.length > 0) {
      setUploadWarnings(allWarnings)
    }

    if (successful.length > 0) {
      setUploaded(successful.map((r) => r.website))
      setAddWebsiteError(null)
    }

    if (failed.length > 0) {
      if (failed.length === 1) {
        setUploadError(
          formatWebsiteUploadError(
            failed[0].error,
            'Error uploading website - please try again.',
          ),
        )
      } else {
        const enhancedErrors = failed
          .map((r) => formatWebsiteFailureLine(r.website, r.error))
          .join('\n')

        const summary =
          successful.length > 0
            ? `${successful.length} website(s) uploaded successfully, but ${failed.length} failed:\n\n${enhancedErrors}`
            : `All ${failed.length} website(s) failed to upload:\n\n${enhancedErrors}`

        setUploadError(summary)
      }

      setWebsites(failed.map((r) => r.website))
    } else {
      setWebsites([])
    }
    setUploading(false)
    setJobId(null)
    refetchDocs()
  }

  useEffect(() => {
    if (uploadProgress.status !== 'completed') return
    const results = uploadProgress.data?.results
    if (Array.isArray(results)) {
      applyUploadResults(results)
    }
  }, [uploadProgress.status, uploadProgress.data])

  // A ref rather than an effect dependency: when this widget is rendered
  // inline in a list (FundingNeedsList), the parent passes a new
  // onPendingChange closure every render. Depending on it directly would
  // re-run the effect below every render, and since the cleanup fires with
  // the previous (stale) closure before the new one runs, that toggles the
  // parent's state false-then-true forever (#1465).
  const onPendingChangeRef = useRef(onPendingChange)
  useEffect(() => {
    onPendingChangeRef.current = onPendingChange
  })

  // `websites` covers both "staged, not yet uploaded" and "upload in flight"
  // (only cleared once the upload completes in applyUploadResults above);
  // `pendingWebsite` covers a URL awaiting the org-website confirmation
  // prompt, before it's even added to that list. The cleanup clears the
  // parent's flag on unmount (e.g. switching upload tabs), since staged
  // URLs are lost with this component and can no longer block.
  useEffect(() => {
    onPendingChangeRef.current?.(websites.length > 0 || pendingWebsite !== null)
    return () => onPendingChangeRef.current?.(false)
  }, [websites.length, pendingWebsite])

  const stagedWebsiteCount = websites.length

  async function handleUpload() {
    setUploading(true)
    setUploaded(null)
    setUploadError(null)
    setUploadWarnings([])

    try {
      const response = await uploadFundingNeedWebsites.mutateAsync({
        token,
        websites,
        orgUuid,
        fundingNeedId,
      })
      setJobId(response.job_id)
    } catch (error) {
      // Fallback error handling for network errors, server errors, etc.

      setUploadError(
        formatWebsiteUploadError(
          error instanceof Error ? error.message : undefined,
          'Error uploading websites - Please try again later.',
        ),
      )
      setUploading(false)
    }
  }

  function handleRemoveWebsite(index: number) {
    setWebsites((prevWebsites) => prevWebsites.filter((_, i) => i !== index))
    setAddWebsiteError(null)
    setUploadError(null)
  }

  function handleAddWebsiteChange(
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) {
    setAddWebsiteError(null)
    setNewWebsite((event.target as HTMLInputElement).value.trim())
  }

  return (
    <Box sx={{ p: 3, width: isMobile ? '100%' : '80%', mx: 'auto', mb: 3 }}>
      <Typography variant="h5" mb={2}>
        Upload Website URLs for this Funding Request
      </Typography>

      <Typography variant="body2" fontWeight={500} mb={2}>
        Website content uploaded here is only used for this funding request -
        The content is not added to your organisation's general profile or any
        other funding request. <br /> If you want to add general content that is
        relevant to all funding requests, you can add it to your organisation's
        profile <Link to="/profile/documents">here</Link>
      </Typography>
      <ListItem>
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <Typography variant="body1" mb={2}>
            How this works:
          </Typography>

          <Typography variant="body2" mb={1}>
            When you upload your organisation's main website address, this tool
            will search through your website and save content from up to 10
            pages. The content of each page saved will be used to generate your
            profile and applications.
          </Typography>
          <Typography variant="body2" mb={2}>
            If you upload a specific page — such as a particular page on your
            own site, a news article, or a partner page — only that page will be
            saved. You can add multiple page addresses, and refresh any of them
            at any time to get the latest content.
          </Typography>

          <Typography variant="body2" mb={1}>
            <span style={{ fontWeight: '500' }}>
              Some sites are not supported.{' '}
            </span>
            <br />
            Social media profiles and any sites that require a login cannot be
            read. For content from these sites, please either save them as PDFs
            and upload them as a file instead, or add the relevant content to
            your organisation description.
          </Typography>
          <Typography variant="body2" mb={3}>
            <span style={{ fontWeight: '500' }}>
              Document Hosting/Digital Publishing Platforms like Issuu, Scribd,
              SlideShare, Flipsnack are also not supported.
            </span>{' '}
            You can upload these documents as files instead.
          </Typography>
        </Box>
      </ListItem>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          justifyContent: 'center',
        }}
      >
        <TextField
          label="Website URL"
          value={newWebsite}
          onChange={(event) => handleAddWebsiteChange(event)}
          sx={{
            width: '100%',
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: '#757575',
            },
          }}
        />
        {newWebsite.trim() !== '' ? (
          <Button
            variant="contained"
            disableRipple
            disabled={uploading || newWebsite === ''}
            onClick={() => handleAddWebsite()}
            sx={{
              mb: 5,
              borderColor: '#1C3045',
              bgcolor: '#1C3045',
              color: '#fff',
              textTransform: 'none',
              fontSize: '16px',
              borderRadius: 2,
              py: 1.5,
              width: '400px',
              mx: 'auto',
            }}
          >
            <Typography
              variant="body1"
              sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
            >
              Add to the list for upload
            </Typography>
          </Button>
        ) : null}

        {addWebsiteError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {addWebsiteError}
          </Alert>
        )}
      </Box>

      {pendingWebsite && (
        <Box
          mt={3}
          p={2}
          sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}
        >
          <Typography variant="subtitle1" fontWeight={600} mb={1}>
            Is this your organisation's official website?
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={0.5}>
            <strong>{pendingWebsite}</strong>
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Only select "Yes" if this is your own domain — not a social media
            profile, news article, or any site that isn't your organisation's
            own. If yes, we'll automatically read through up to 10 pages on this
            website and save each one separately.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              onClick={handleConfirmIsOrgWebsite}
              disabled={updateOrgWebsite.isPending}
              sx={{
                bgcolor: '#1C3045',
                color: '#fff',
                textTransform: 'none',
                borderRadius: 2,
              }}
            >
              Yes, save as my website
            </Button>
            <Button
              variant="outlined"
              onClick={handleConfirmNotOrgWebsite}
              disabled={updateOrgWebsite.isPending}
              sx={{
                border: '2px solid #1C3045',
                color: '#1C3045',
                textTransform: 'none',
                borderRadius: 2,
              }}
            >
              No, just save this page
            </Button>
          </Box>
        </Box>
      )}

      {websites.length > 0 && (
        // group pending uploads
        <Box
          mt={3}
          sx={(theme) => ({
            p: { xs: 1.5, sm: 2 },
            borderRadius: 2,
            // Same derivation as the staged-upload caption elsewhere in this
            // flow, so the two read as one system rather than two tints.
            bgcolor: lighten(theme.palette.warning.light, 0.9),
            border: `1px solid ${lighten(theme.palette.warning.light, 0.7)}`,
          })}
        >
          <Typography variant="subtitle1" fontWeight={600}>
            Website URLs ready to upload ({stagedWebsiteCount})
          </Typography>
          <Typography
            variant="caption"
            sx={{ fontStyle: 'italic', color: 'text.secondary' }}
          >
            Click the Upload button below to continue, or remove the website(s)
            from the list to cancel the upload
          </Typography>
          <List>
            {websites.map(
              (website, index) =>
                website !== '' && (
                  <ListItem
                    key={index}
                    disableGutters={isMobile}
                    secondaryAction={
                      <IconButton
                        edge="end"
                        onClick={() => handleRemoveWebsite(index)}
                        disabled={uploading}
                        aria-label={`Remove ${website} from the upload list`}
                      >
                        <DeleteOutlineIcon />
                      </IconButton>
                    }
                  >
                    <ListItemText
                      primary={website}
                      slotProps={{
                        primary: { sx: { overflowWrap: 'anywhere' } },
                      }}
                    />
                  </ListItem>
                ),
            )}
          </List>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              mx: 'auto',
              mt: 2,
              width: '100%',
              maxWidth: '600px',
            }}
          >
            <Button
              variant="contained"
              disableRipple
              onClick={handleUpload}
              disabled={uploading}
              sx={{
                width: '100%',
                maxWidth: 400,
                mx: 'auto',
                bgcolor: '#1C3045',
                color: '#fff',
                textTransform: 'none',
                boxShadow: 'none',
                borderRadius: 3,
                gap: 2,
                fontSize: '16px',
                '&:hover': {
                  bgcolor: '#16283a',
                  boxShadow: 'none',
                },
                '&:focus': {
                  outline: '2px solid #1C3045',
                  outlineOffset: '2px',
                },
                '&.Mui-disabled': {
                  bgcolor: '#1C3045',
                  color: '#fff',
                  opacity: 0.7,
                },
              }}
            >
              {uploading ? (
                <>
                  <CircularProgress size={20} color="inherit" />
                  <span>
                    Getting and uploading website content...{' '}
                    {uploadProgress.progress}%
                  </span>
                </>
              ) : (
                `Upload ${stagedWebsiteCount} website URL${
                  stagedWebsiteCount === 1 ? '' : 's'
                }`
              )}
            </Button>
          </Box>
        </Box>
      )}

      {uploaded && (
        <Alert severity="success" sx={{ mt: 3 }}>
          Websites uploaded successfully!
        </Alert>
      )}
      {uploadWarnings.length > 0 && (
        <Alert severity="warning" sx={{ mt: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
            Some pages were skipped:
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 2 }}>
            {uploadWarnings.map((warning, index) => (
              <li key={index}>
                <Typography variant="body2">{warning}</Typography>
              </li>
            ))}
          </Box>
        </Alert>
      )}
      {uploadError && (
        <Alert severity="error" sx={{ mt: 3 }}>
          <Box
            component="pre"
            sx={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit', margin: 0 }}
          >
            {uploadError}
          </Box>
        </Alert>
      )}
    </Box>
  )
}
